import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Truck,
  Receipt,
  Package,
  Calendar,
  User,
  MapPin,
  Phone,
  Mail,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { format } from "date-fns";
import toast from "react-hot-toast";
import apiService from "../services/api";
import LoadingSpinner from "../components/ui/LoadingSpinner";

const ViewPurchaseOrder = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const orderData = await apiService.getOrder(orderId);
      console.log("Order data with invoices and payments:", {
        invoices: orderData.invoices,
        invoicesCount: orderData.invoices?.length,
        paymentsPerInvoice: orderData.invoices?.map((inv) => ({
          invoiceNumber: inv.invoiceNumber,
          payments: inv.payments,
          paymentsCount: inv.payments?.length,
        })),
      });
      setOrder(orderData);
    } catch (error) {
      console.error("Error fetching order:", error);
      toast.error("Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      pending:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
      submitted:
        "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
      approved:
        "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
      processing:
        "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400",
      invoiced:
        "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
      delivered:
        "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
      dispatched:
        "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400",
      rejected: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
      cancelled:
        "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
    };
    return statusColors[status?.toLowerCase()] || statusColors.pending;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      submitted: AlertCircle,
      approved: CheckCircle,
      processing: Package,
      invoiced: Receipt,
      delivered: CheckCircle,
      dispatched: Truck,
      rejected: XCircle,
      cancelled: XCircle,
    };
    const Icon = icons[status?.toLowerCase()] || Clock;
    return <Icon className="w-4 h-4" />;
  };

  const calculateDeliveryProgress = () => {
    if (!order?.orderItems) return { delivered: 0, total: 0, percentage: 0 };

    const totalOrdered = order.orderItems.reduce(
      (sum, item) => sum + item.quantity,
      0
    );

    let totalDelivered = 0;
    if (order.deliveryOrders) {
      order.orderItems.forEach((orderItem) => {
        let itemDelivered = 0;
        order.deliveryOrders.forEach((deliveryOrder) => {
          // Only count items from delivery orders with status "delivered"
          if (
            deliveryOrder.deliveryStatus === "delivered" &&
            deliveryOrder.items
          ) {
            deliveryOrder.items.forEach((deliveryItem) => {
              if (
                deliveryItem.orderItem?.productSkuId ===
                  orderItem.productSkuId ||
                deliveryItem.productSkuId === orderItem.productSkuId
              ) {
                itemDelivered += deliveryItem.quantity || 0;
              }
            });
          }
        });
        totalDelivered += itemDelivered;
      });
    }

    const percentage =
      totalOrdered > 0 ? (totalDelivered / totalOrdered) * 100 : 0;
    return { delivered: totalDelivered, total: totalOrdered, percentage };
  };

  const calculateInvoiceProgress = () => {
    if (!order?.deliveryOrders) return { invoiced: 0, total: 0, percentage: 0 };

    const total = order.deliveryOrders.length;
    const invoiced = order.deliveryOrders.filter((do_) => do_.invoiced).length;
    const percentage = total > 0 ? (invoiced / total) * 100 : 0;

    return { invoiced, total, percentage };
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!order) {
    return (
      <div className="p-6">
        <div className="text-center text-red-600">Order not found</div>
      </div>
    );
  }

  const deliveryProgress = calculateDeliveryProgress();
  const invoiceProgress = calculateInvoiceProgress();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 shadow-sm border-b border-gray-200 dark:border-slate-700">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/admin/orders")}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white text-left">
                  Purchase Order Details
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 text-left">
                  {order.poNumber || order.orderNumber}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor(
                  order.status
                )}`}
              >
                {getStatusIcon(order.status)}
                <span className="capitalize">{order.status}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Order Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Total Amount */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Amount
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  ₹{order.totalAmount?.toLocaleString() || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <Receipt className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Items
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {order.orderItems?.length || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          {/* Delivery Orders */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Delivery Orders
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {order.deliveryOrders?.length || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                <Truck className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          {/* Invoices */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Invoices
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {order.invoices?.length || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Delivery Progress */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
              Delivery Progress
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  {deliveryProgress.delivered} of {deliveryProgress.total} units
                  delivered
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {deliveryProgress.percentage.toFixed(0)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2.5">
                <div
                  className="bg-green-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${deliveryProgress.percentage}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Invoice Progress */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
              Invoice Progress
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  {invoiceProgress.invoiced} of {invoiceProgress.total} DOs
                  invoiced
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {invoiceProgress.percentage.toFixed(0)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2.5">
                <div
                  className="bg-orange-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${invoiceProgress.percentage}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Payment Progress */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
              Payment Progress
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  ₹
                  {(() => {
                    const totalPaid =
                      order.invoices?.reduce((sum, inv) => {
                        const invoicePaid =
                          inv.payments?.reduce(
                            (pSum, p) => pSum + (p.amountPaid || 0),
                            0
                          ) || 0;
                        return sum + invoicePaid;
                      }, 0) || 0;
                    return totalPaid.toLocaleString();
                  })()}{" "}
                  of ₹{order.totalAmount?.toLocaleString() || 0}
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {(() => {
                    const totalPaid =
                      order.invoices?.reduce((sum, inv) => {
                        const invoicePaid =
                          inv.payments?.reduce(
                            (pSum, p) => pSum + (p.amountPaid || 0),
                            0
                          ) || 0;
                        return sum + invoicePaid;
                      }, 0) || 0;
                    const percentage =
                      order.totalAmount > 0
                        ? (totalPaid / order.totalAmount) * 100
                        : 0;
                    return percentage.toFixed(0);
                  })()}
                  %
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{
                    width: `${(() => {
                      const totalPaid =
                        order.invoices?.reduce((sum, inv) => {
                          const invoicePaid =
                            inv.payments?.reduce(
                              (pSum, p) => pSum + (p.amountPaid || 0),
                              0
                            ) || 0;
                          return sum + invoicePaid;
                        }, 0) || 0;
                      const percentage =
                        order.totalAmount > 0
                          ? (totalPaid / order.totalAmount) * 100
                          : 0;
                      return percentage;
                    })()}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            Customer Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Business Name
                </p>
                <p className="text-base font-medium text-gray-900 dark:text-white">
                  {order.customer?.businessName ||
                    order.customer?.name ||
                    "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Contact Person
                </p>
                <p className="text-base text-gray-900 dark:text-white">
                  {order.customer?.contactName || "N/A"}
                </p>
              </div>
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Phone
                  </p>
                  <p className="text-base text-gray-900 dark:text-white">
                    {order.customer?.contactMobile || "N/A"}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Email
                  </p>
                  <p className="text-base text-gray-900 dark:text-white">
                    {order.customer?.email || "N/A"}
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Billing Address
                  </p>
                  <p className="text-base text-gray-900 dark:text-white">
                    {order.customer?.billAddressLine || "N/A"}
                    <br />
                    {order.customer?.billDistrict}, {order.customer?.billState}{" "}
                    {order.customer?.billPinCode}
                    <br />
                    {order.customer?.billCountry || "India"}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  GST Number
                </p>
                <p className="text-base font-mono text-gray-900 dark:text-white">
                  {order.customer?.gstNumber || "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Package className="w-5 h-5" />
            Order Items
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    SKU
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Unit Price
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Delivered
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                {order.orderItems?.map((item, index) => {
                  // Calculate delivered quantity for this item
                  let deliveredQty = 0;
                  if (order.deliveryOrders) {
                    order.deliveryOrders.forEach((deliveryOrder) => {
                      if (deliveryOrder.items) {
                        deliveryOrder.items.forEach((deliveryItem) => {
                          if (
                            deliveryItem.orderItem?.productSkuId ===
                              item.productSkuId ||
                            deliveryItem.productSkuId === item.productSkuId
                          ) {
                            deliveredQty += deliveryItem.quantity || 0;
                          }
                        });
                      }
                    });
                  }

                  return (
                    <tr key={index}>
                      <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                        {item.productSku?.name ||
                          item.productSku?.description ||
                          "N/A"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {item.productSku?.sku || "N/A"}
                      </td>
                      <td className="px-4 py-4 text-sm text-right text-gray-900 dark:text-white">
                        {item.quantity}
                      </td>
                      <td className="px-4 py-4 text-sm text-right text-gray-900 dark:text-white">
                        ₹{item.price?.toFixed(2) || "0.00"}
                      </td>
                      <td className="px-4 py-4 text-sm text-right font-medium text-gray-900 dark:text-white">
                        ₹{(item.price * item.quantity).toFixed(2)}
                      </td>
                      <td className="px-4 py-4 text-sm text-center">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            deliveredQty >= item.quantity
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                              : deliveredQty > 0
                              ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                              : "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                          }`}
                        >
                          {deliveredQty} / {item.quantity}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Delivery Orders */}
        {order.deliveryOrders && order.deliveryOrders.length > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Delivery Orders ({order.deliveryOrders.length})
            </h2>
            <div className="space-y-4">
              {order.deliveryOrders.map((deliveryOrder) => (
                <div
                  key={deliveryOrder.id}
                  className="border border-gray-200 dark:border-slate-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {deliveryOrder.deliveryNumber}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {deliveryOrder.items?.length || 0} items •{" "}
                        {deliveryOrder.expectedDeliveryDate &&
                          format(
                            new Date(deliveryOrder.expectedDeliveryDate),
                            "dd MMM yyyy"
                          )}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          deliveryOrder.deliveryStatus
                        )}`}
                      >
                        {deliveryOrder.deliveryStatus}
                      </span>
                      {deliveryOrder.invoiced && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 rounded-full text-xs font-medium">
                          Invoiced
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">
                        Delivery Method
                      </p>
                      <p className="text-gray-900 dark:text-white capitalize">
                        {deliveryOrder.deliveryMethod || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">
                        Expected Date
                      </p>
                      <p className="text-gray-900 dark:text-white">
                        {deliveryOrder.expectedDeliveryDate
                          ? format(
                              new Date(deliveryOrder.expectedDeliveryDate),
                              "dd MMM yyyy"
                            )
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">
                        Actual Date
                      </p>
                      <p className="text-gray-900 dark:text-white">
                        {deliveryOrder.actualDeliveryDate
                          ? format(
                              new Date(deliveryOrder.actualDeliveryDate),
                              "dd MMM yyyy"
                            )
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                  {deliveryOrder.items && deliveryOrder.items.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Delivered Items:
                      </p>
                      <div className="space-y-2">
                        {deliveryOrder.items.map((item, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between text-sm bg-gray-50 dark:bg-slate-900/50 rounded px-3 py-2"
                          >
                            <div className="flex-1">
                              <span className="text-gray-900 dark:text-white font-medium">
                                {item.orderItem?.productSku?.name ||
                                  item.orderItem?.productSku?.description ||
                                  "Product"}
                              </span>
                              <span className="text-gray-500 dark:text-gray-400 ml-2">
                                ({item.orderItem?.productSku?.sku || "N/A"})
                              </span>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className="text-gray-600 dark:text-gray-400">
                                Qty:{" "}
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {item.quantity}
                                </span>
                              </span>
                              <span className="text-gray-600 dark:text-gray-400">
                                ₹
                                {(
                                  (item.orderItem?.price || 0) * item.quantity
                                ).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {deliveryOrder.deliveryNotes && (
                    <div className="mt-3 text-sm">
                      <p className="text-gray-600 dark:text-gray-400">Notes:</p>
                      <p className="text-gray-900 dark:text-white">
                        {deliveryOrder.deliveryNotes}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Invoices */}
        {order.invoices && order.invoices.length > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Invoices ({order.invoices.length})
            </h2>
            <div className="space-y-4">
              {order.invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="border border-gray-200 dark:border-slate-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {invoice.invoiceNumber}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {invoice.invoiceDate &&
                          format(new Date(invoice.invoiceDate), "dd MMM yyyy")}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          invoice.status
                        )}`}
                      >
                        {invoice.status}
                      </span>
                      <span className="text-lg font-bold text-gray-900 dark:text-white">
                        ₹{invoice.totalAmount?.toLocaleString() || 0}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">
                        Due Date
                      </p>
                      <p className="text-gray-900 dark:text-white">
                        {invoice.dueDate
                          ? format(new Date(invoice.dueDate), "dd MMM yyyy")
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">
                        Payment Terms
                      </p>
                      <p className="text-gray-900 dark:text-white">
                        {invoice.paymentTerms || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">
                        Subtotal
                      </p>
                      <p className="text-gray-900 dark:text-white">
                        ₹{invoice.subtotal?.toLocaleString() || 0}
                      </p>
                    </div>
                  </div>
                  {/* Show related delivery orders from database relationship */}
                  {invoice.deliveryOrders &&
                    invoice.deliveryOrders.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Delivery Orders Invoiced:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {invoice.deliveryOrders.map((relation) => (
                            <span
                              key={relation.deliveryOrder.id}
                              className="px-3 py-1.5 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg text-xs font-medium flex items-center gap-2"
                            >
                              <Truck className="w-3 h-3" />
                              {relation.deliveryOrder.deliveryNumber}
                              {relation.deliveryOrder.deliveryStatus ===
                                "delivered" && (
                                <CheckCircle className="w-3 h-3 text-green-600 dark:text-green-400" />
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  {invoice.notes && (
                    <div className="mt-3 text-sm">
                      <p className="text-gray-600 dark:text-gray-400">Notes:</p>
                      <p className="text-gray-900 dark:text-white">
                        {invoice.notes}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Order Timeline */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Order Timeline
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  Order Created
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {order.createdAt &&
                    format(new Date(order.createdAt), "dd MMM yyyy, hh:mm a")}
                </p>
              </div>
            </div>
            {order.updatedAt && (
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                  <Clock className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Last Updated
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {format(new Date(order.updatedAt), "dd MMM yyyy, hh:mm a")}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewPurchaseOrder;
